/**
 * Airtable Cache Debugging Module
 * 
 * Provides tools for monitoring and debugging the Airtable cache
 * Exports functions for displaying cache statistics, clearing cache,
 * and testing cache performance
 */

import { getCacheStats, clearCache } from './cache';
import { preloadTables } from './rest-api';

/**
 * Formats duration in milliseconds to a human-readable string
 */
function formatDuration(ms: number): string {
  if (ms < 1000) {
    return `${ms.toFixed(2)}ms`;
  } else if (ms < 60000) {
    return `${(ms / 1000).toFixed(2)}s`;
  } else {
    return `${(ms / 60000).toFixed(2)}min`;
  }
}

/**
 * Display current cache statistics in a formatted way
 */
export async function displayCacheStats(): Promise<Record<string, any>> {
  const stats = await getCacheStats();
  const formattedStats = {
    items: stats.totalItems,
    size: formatBytes(stats.totalSize),
    hitRatio: `${(stats.hitRatio * 100).toFixed(1)}%`,
    hits: stats.hits,
    misses: stats.misses,
    evictions: stats.evictions,
    averageAge: formatDuration(stats.averageItemAge * 1000),
    oldestItem: stats.oldestItem ? new Date(stats.oldestItem).toISOString() : 'None',
    newestItem: stats.newestItem ? new Date(stats.newestItem).toISOString() : 'None',
  };
  console.log('📊 Airtable Cache Statistics:');
  console.table(formattedStats);
  return formattedStats;
}

/**
 * Format bytes to human-readable string
 */
function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

/**
 * Reset the cache and reload tables
 */
export async function resetCache(): Promise<void> {
  console.log('🔄 Resetting Airtable cache...');
  
  // Clear the cache
  await clearCache();
  
  // Reload preload tables if configured
  const { airtableConfig } = await import('./config');
  if (airtableConfig.cache.preloadTables.length > 0) {
    const result = await preloadTables();
    console.log(`🔄 Preloaded ${result.loaded}/${airtableConfig.cache.preloadTables.length} tables`);
  }
  
  // Display new stats
  await displayCacheStats();
}

/**
 * Test cache performance by performing a sequence of operations
 */
export async function testCachePerformance(
  testFn: () => Promise<any>, 
  iterations: number = 5
): Promise<{ withCache: number; withoutCache: number; improvement: string }> {
  console.log(`🧪 Testing cache performance (${iterations} iterations)...`);
  
  // First run: without cache
  console.log('Running test without cache...');
  await clearCache(); // Ensure cache is empty
  
  const startWithoutCache = Date.now();
  for (let i = 0; i < iterations; i++) {
    await testFn();
  }
  const timeWithoutCache = Date.now() - startWithoutCache;
  
  // Second run: with cache
  console.log('Running test with cache...');
  const startWithCache = Date.now();
  for (let i = 0; i < iterations; i++) {
    await testFn();
  }
  const timeWithCache = Date.now() - startWithCache;
  
  // Calculate improvement
  const improvement = (1 - (timeWithCache / timeWithoutCache)) * 100;
  
  const results = {
    withCache: timeWithCache,
    withoutCache: timeWithoutCache,
    improvement: `${improvement.toFixed(1)}%`
  };
  
  console.log('🧪 Performance Test Results:');
  console.log(`Without cache: ${formatDuration(timeWithoutCache)}`);
  console.log(`With cache: ${formatDuration(timeWithCache)}`);
  console.log(`Improvement: ${results.improvement}`);
  
  return results;
}

/**
 * Analyze cache usage by table
 * This helps identify which tables benefit most from caching
 */
export function analyzeCacheByTable(): Record<string, any> {
  // This would require deeper integration with the cache internals
  // For now, providing a placeholder that would report to console
  console.log('📊 Cache usage by table not implemented in this version');
  
  return {
    // This would include table-specific metrics in a real implementation
    message: 'Table-specific cache analytics would appear here'
  };
}

/**
 * Enable/disable debug mode dynamically
 */
export async function setDebugMode(enabled: boolean): Promise<void> {
  const { airtableConfig } = await import('./config');
  airtableConfig.cache.debug = enabled;
  console.log(`🐛 Cache debug mode: ${enabled ? 'ENABLED' : 'DISABLED'}`);
} 
